import React, { useState, useEffect } from 'react';
import { supabaseAdmin } from '../../lib/supabaseAdmin';

const KeywordsGenerator = ({ onArticleGenerated }) => {
    const [keywords, setKeywords] = useState([]);
    const [filteredKeywords, setFilteredKeywords] = useState([]);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('searches-desc');
    const [loading, setLoading] = useState(true);
    const [selectedKeyword, setSelectedKeyword] = useState(null);
    const [categories, setCategories] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [status, setStatus] = useState(null);
    const [genOptions, setGenOptions] = useState({
        categoryId: '',
        wordCount: 1500,
    });

    useEffect(() => {
        const fetchData = async () => {
            const [kwResult, catResult] = await Promise.all([
                supabaseAdmin.from('keywords').select('*'),
                supabaseAdmin.from('categories').select('id, name').order('name'),
            ]);

            if (kwResult.data) {
                // Deduplicate by keyword text
                const seen = new Set();
                const unique = kwResult.data.filter(kw => {
                    const lower = kw.keyword.toLowerCase();
                    if (seen.has(lower)) return false;
                    seen.add(lower);
                    return true;
                });
                setKeywords(unique);
                setFilteredKeywords(unique);
            }
            if (catResult.data) setCategories(catResult.data);
            setLoading(false);
        };
        fetchData();
    }, []);

    useEffect(() => {
        let result = [...keywords];

        // Filter by search
        if (search) {
            const lower = search.toLowerCase();
            result = result.filter(kw => kw.keyword.toLowerCase().includes(lower));
        }

        // Sort
        switch (sortBy) {
            case 'searches-desc':
                result.sort((a, b) => parseInt(b.monthly_searches) - parseInt(a.monthly_searches));
                break;
            case 'searches-asc':
                result.sort((a, b) => parseInt(a.monthly_searches) - parseInt(b.monthly_searches));
                break;
            case 'competition-asc':
                result.sort((a, b) => parseFloat(a.competition) - parseFloat(b.competition));
                break;
            case 'competition-desc':
                result.sort((a, b) => parseFloat(b.competition) - parseFloat(a.competition));
                break;
            case 'keyword-asc':
                result.sort((a, b) => a.keyword.localeCompare(b.keyword));
                break;
            default:
                break;
        }

        setFilteredKeywords(result);
    }, [search, sortBy, keywords]);

    const formatNumber = (num) => {
        const n = parseInt(num);
        if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
        if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
        return n.toString();
    };

    const handleGenerate = async () => {
        if (!selectedKeyword) return;

        setGenerating(true);
        setStatus(null);

        try {
            const categoryName = genOptions.categoryId
                ? categories.find(c => c.id === genOptions.categoryId)?.name
                : '';

            const response = await fetch('/.netlify/functions/generate-article', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    keyword: selectedKeyword.keyword,
                    categoryName,
                    targetWordCount: genOptions.wordCount,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to generate article');
            }

            const articleData = await response.json();

            // Pass the generated data to the article editor
            onArticleGenerated({
                title: articleData.title,
                description: articleData.description,
                slug: articleData.slug,
                content: articleData.content,
                category_id: genOptions.categoryId,
            });

            setStatus({ type: 'success', message: 'Article generated! Redirecting to editor...' });
        } catch (error) {
            console.error('Generation error:', error);
            setStatus({ type: 'error', message: error.message });
        } finally {
            setGenerating(false);
        }
    };

    if (loading) return <div className="empty-state"><p>Loading keywords...</p></div>;

    return (
        <div className="keywords-generator">
            <h2>AI Article Generator</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '1.5rem' }}>
                Pick a keyword, choose a category, and let AI generate an article for you to review and publish.
            </p>

            {status && (
                <div className={`admin-status ${status.type}`}>{status.message}</div>
            )}

            <div className="keywords-controls">
                <input
                    type="text"
                    className="keywords-search"
                    placeholder="Search keywords..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <select
                    className="keywords-sort"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                >
                    <option value="searches-desc">Searches: High → Low</option>
                    <option value="searches-asc">Searches: Low → High</option>
                    <option value="competition-asc">Competition: Low → High</option>
                    <option value="competition-desc">Competition: High → Low</option>
                    <option value="keyword-asc">Keyword: A → Z</option>
                </select>
            </div>

            <div className="keywords-table-wrapper">
                <table className="keywords-table">
                    <thead>
                        <tr>
                            <th>Keyword</th>
                            <th>Monthly Searches</th>
                            <th>Competition</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredKeywords.map((kw) => (
                            <tr
                                key={kw.id}
                                style={selectedKeyword?.id === kw.id ? { backgroundColor: 'rgba(133, 110, 231, 0.15)' } : {}}
                            >
                                <td className="keyword-text">{kw.keyword}</td>
                                <td className="searches">{formatNumber(kw.monthly_searches)}</td>
                                <td className="competition">{parseFloat(kw.competition).toFixed(0)}%</td>
                                <td>
                                    <button
                                        className="btn-secondary"
                                        onClick={() => setSelectedKeyword(kw)}
                                    >
                                        Select
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {selectedKeyword && (
                <div className="ai-generation-panel">
                    <h3>Generate Article</h3>
                    <div className="selected-keyword">
                        Keyword: <strong>{selectedKeyword.keyword}</strong>
                        {' '}({formatNumber(selectedKeyword.monthly_searches)} searches/mo)
                    </div>

                    <div className="ai-generation-options">
                        <div className="form-group">
                            <label>Category</label>
                            <select
                                value={genOptions.categoryId}
                                onChange={(e) => setGenOptions(prev => ({ ...prev, categoryId: e.target.value }))}
                            >
                                <option value="">— Select Category —</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Target Word Count</label>
                            <select
                                value={genOptions.wordCount}
                                onChange={(e) => setGenOptions(prev => ({ ...prev, wordCount: parseInt(e.target.value) }))}
                            >
                                <option value={800}>~800 words</option>
                                <option value={1200}>~1,200 words</option>
                                <option value={1500}>~1,500 words</option>
                                <option value={2000}>~2,000 words</option>
                                <option value={2500}>~2,500 words</option>
                            </select>
                        </div>
                    </div>

                    {generating ? (
                        <div className="generating-status">
                            <div className="spinner"></div>
                            Generating article... this may take 30-60 seconds
                        </div>
                    ) : (
                        <button className="btn-primary" onClick={handleGenerate}>
                            Generate Article
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default KeywordsGenerator;
